import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Calculate the bottom position of the header component
 * @returns {number} Bottom position in pixels
 */
function getHeaderBottomPosition() {
  const header = document.querySelector('#header-component');
  if (!header) return 0;
  
  const headerRect = header.getBoundingClientRect();
  return headerRect.bottom;
}

/**
 * Update CSS custom properties for menu positioning
 */
function updateMenuPositioning() {
  const headerBottom = getHeaderBottomPosition();
  document.body.style.setProperty('--header-bottom-position', `${headerBottom}px`);
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMobileTabs();
    this.#setupMobileGroups();

    // Update positioning on scroll for sticky headers
    window.addEventListener('scroll', updateMenuPositioning, { passive: true });
    window.addEventListener('resize', updateMenuPositioning, { passive: true });

    // Initial positioning update
    updateMenuPositioning();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('scroll', updateMenuPositioning);
    window.removeEventListener('resize', updateMenuPositioning);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Update positioning before opening
    updateMenuPositioning();

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });

    details.removeAttribute('open');
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup mobile tabs functionality
   */
  #setupMobileTabs() {
    const tabs = this.querySelectorAll('.mobile-drawer-tab');
    const tabContents = this.querySelectorAll('.mobile-drawer-tab-content');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {

        tabs.forEach(t => {
          t.classList.remove('mobile-drawer-tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tabContents.forEach(content => {
          content.classList.remove('mobile-drawer-tab-content--active');
        });

        tab.classList.add('mobile-drawer-tab--active');
        tab.setAttribute('aria-selected', 'true');
        
        if (tab instanceof HTMLElement) {
          const tabIndex = tab.dataset.tabIndex;
          const targetContent = this.querySelector(`#tab-content-${tabIndex}`);
          if (targetContent) {
            targetContent.classList.add('mobile-drawer-tab-content--active');
          }
        }
      });
    });
  }

  /**
   * Setup mobile drawer collapsible groups
   */
  #setupMobileGroups() {
    const groupHeaders = this.querySelectorAll('.desktop-drawer-group-header');
    
    groupHeaders.forEach(header => {
      header.addEventListener('click', (e) => {
        e.preventDefault();
        
        const group = header.parentElement;
        if (!group) return;
        const isExpanded = header.getAttribute('aria-expanded') === 'true';
        
        if (!isExpanded) {
          const allGroups = this.querySelectorAll('.desktop-drawer-group');
          const allHeaders = this.querySelectorAll('.desktop-drawer-group-header');
          
            allGroups.forEach(otherGroup => {
              if (otherGroup !== group) {
                otherGroup.classList.remove('desktop-drawer-group--expanded');
                const ul = otherGroup.querySelector('ul');
                if (ul) {
                  ul.style.maxHeight = '';
                }
              }
            });          allHeaders.forEach(otherHeader => {
            if (otherHeader !== header) {
              otherHeader.setAttribute('aria-expanded', 'false');
            }
          });
        }
        
        header.setAttribute('aria-expanded', (!isExpanded).toString());
        group.classList.toggle('desktop-drawer-group--expanded', !isExpanded);
        const ul = group.querySelector('ul');
        if (ul && !isExpanded) {
          ul.style.maxHeight = ul.scrollHeight + 'px';
        }
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  if (element.hasAttribute('open')) {
    element.removeAttribute('open');
  }
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
